import { createContext, Dispatch, SetStateAction, useState } from 'react'
import {
  ApiError,
  ApiResponse,
  AxiosResponseExtended,
  CategoryData,
  ContextProviderProps,
  DataObject,
  SelectedConfigItem,
  TablePaginationParams,
} from 'config/types'
import useToast from 'hooks/useToast'
import { UseMutateFunction } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { AddUpdateForm } from 'config/types/configurator'
import {
  ConfirmItem,
  confirmItemData,
  MutateFunctionNumber,
} from 'components/modal'
import { paginationParam } from 'config/UIHelpers'
import { updateFieldDTO } from 'config/types/field'
import { Levels } from 'config/enums'

type UpdateFormType = UseMutateFunction<
  AxiosResponse<any, any>,
  unknown,
  AddUpdateForm,
  unknown
>

type UpdateFieldFormType = UseMutateFunction<
  AxiosResponse<any, any>,
  unknown,
  updateFieldDTO,
  unknown
>

interface ConfiguratorType {
  categoryParams: TablePaginationParams
  setCategoryParams: Dispatch<SetStateAction<TablePaginationParams>>
  subCategoryParams: TablePaginationParams
  setSubCategoryParams: Dispatch<SetStateAction<TablePaginationParams>>
  sectionsParams: TablePaginationParams
  setSectionsParams: Dispatch<SetStateAction<TablePaginationParams>>
  sectionFieldsParams: TablePaginationParams
  setSectionFieldsParams: Dispatch<SetStateAction<TablePaginationParams>>
  typeParams: TablePaginationParams
  setTypeParams: Dispatch<SetStateAction<TablePaginationParams>>
  selectedMetaData: CategoryData | null
  setSelectedMetaData: Dispatch<SetStateAction<CategoryData | null>>
  selectedTitle: SelectedConfigItem[]
  setSelectedTitle: Dispatch<SetStateAction<Array<SelectedConfigItem>>>
  confirmation: ConfirmItem
  setConfirmation: Dispatch<SetStateAction<ConfirmItem>>
  currentModalLevel: number
  setCurrentModalLevel: Dispatch<SetStateAction<number>>
  openModals: boolean[]
  setOpenModals: Dispatch<SetStateAction<boolean[]>>
  idArray: number[]
  setIdArray: Dispatch<SetStateAction<number[]>>
  loadingMore: boolean
  setLoadingMore: Dispatch<SetStateAction<boolean>>
  switchToType: boolean
  setSwitchToType: Dispatch<SetStateAction<boolean>>
  copiedItem: boolean
  setCopiedItem: Dispatch<SetStateAction<boolean>>
  handleDelete: (id: number, level: number, func: MutateFunctionNumber) => void
  handleItemDelete: (id: number, func: MutateFunctionNumber) => void
  handleItemEdit: (
    id: number,
    level: number,
    data: CategoryData,
    func: UpdateFormType | UpdateFieldFormType
  ) => void
  handleStatusToggle: (
    id: number,
    status: boolean,
    func: UpdateFormType | UpdateFieldFormType
  ) => void
  handleItemAdd: (id: number, level: number) => void
  closeAllModals: () => void
  handleOpenModals: (id: number, open: boolean, level: number) => void
  handleClickEditItem: (level: number) => void
  handleModalClose: () => void
}

const ConfiguratorContext = createContext<ConfiguratorType>({
  categoryParams: paginationParam,
  setCategoryParams: () => {},
  subCategoryParams: paginationParam,
  setSubCategoryParams: () => {},
  sectionsParams: paginationParam,
  setSectionsParams: () => {},
  sectionFieldsParams: paginationParam,
  typeParams: paginationParam,
  setTypeParams: () => {},
  setSectionFieldsParams: () => {},
  selectedMetaData: null,
  setSelectedMetaData: () => {},
  selectedTitle: [],
  setSelectedTitle: () => [],
  confirmation: confirmItemData,
  setConfirmation: () => {},
  currentModalLevel: 0,
  setCurrentModalLevel: () => {},
  openModals: [],
  setOpenModals: () => [],
  idArray: [],
  setIdArray: () => [],
  loadingMore: false,
  setLoadingMore: () => {},
  switchToType: false,
  setSwitchToType: () => {},
  copiedItem: false,
  setCopiedItem: () => {},
  handleDelete: () => {},
  handleItemDelete: () => {},
  handleItemEdit: () => {},
  handleStatusToggle: () => {},
  handleItemAdd: () => {},
  closeAllModals: () => {},
  handleOpenModals: () => {},
  handleClickEditItem: () => {},
  handleModalClose: () => {},
})

const ConfiguratorProvider = ({ children }: ContextProviderProps) => {
  const maxLevel = 3
  const toast = useToast()
  const [categoryParams, setCategoryParams] =
    useState<TablePaginationParams>(paginationParam)
  const [subCategoryParams, setSubCategoryParams] =
    useState<TablePaginationParams>(paginationParam)
  const [sectionsParams, setSectionsParams] =
    useState<TablePaginationParams>(paginationParam)
  const [sectionFieldsParams, setSectionFieldsParams] =
    useState<TablePaginationParams>(paginationParam)
  const [typeParams, setTypeParams] =
    useState<TablePaginationParams>(paginationParam)
  const [selectedMetaData, setSelectedMetaData] = useState<CategoryData | null>(
    null
  )
  const [currentModalLevel, setCurrentModalLevel] = useState<number>(0)
  const [confirmation, setConfirmation] = useState<ConfirmItem>(confirmItemData)
  const [openModals, setOpenModals] = useState<boolean[]>(
    Array(maxLevel).fill(false)
  )
  const [selectedTitle, setSelectedTitle] = useState<Array<SelectedConfigItem>>(
    []
  )
  const [idArray, setIdArray] = useState<number[]>([])
  const [switchToType, setSwitchToType] = useState<boolean>(false)
  const [copiedItem, setCopiedItem] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const closeAllModals = () => {
    setOpenModals(Array(maxLevel).fill(false))
    setSelectedTitle([])
    setCurrentModalLevel(Levels.category)
  }
  const handleOpenModals = (id: number, value: boolean, level: number) => {
    setOpenModals(prevModals => {
      const newModals = prevModals.map((modal, index) => index < level)
      newModals[level - 1] = value
      return newModals
    })
    setCurrentModalLevel(level)
  }
  const handleClickEditItem = (currentLevel: number) => {
    setOpenModals(prevModals => {
      const newModals = [...prevModals]
      if (currentModalLevel !== currentLevel) {
        newModals[currentLevel - 1] = false
      }
      return newModals
    })
  }

  const handleModalClose = () => {
    const nonNullCount = selectedTitle.filter(item => item !== null).length
    setOpenModals(prevModals => {
      const newModals = [...prevModals]
      newModals[nonNullCount - 1] = true
      return newModals
    })
  }
  const handleDelete = (
    id: number,
    level: number,
    func: UseMutateFunction<AxiosResponse<any, any>, unknown, number, unknown>
  ) => {
    setConfirmation({ id, show: true, level, func })
  }
  const handleItemDelete = (id: number, func?: MutateFunctionNumber) => {
    func?.(id, {
      onSuccess: (response: AxiosResponseExtended<DataObject<{}>>) => {
        toast.success(String(response?.message))
        setConfirmation(confirmItemData)
      },
      onError: error => {
        toast.error((error as ApiError)?.message)
      },
    })
  }
  const handleItemEdit = (
    id: number,
    level: number,
    data: CategoryData,
    func: UpdateFormType | UpdateFieldFormType
  ) => {
    setSelectedMetaData(data)
    handleClickEditItem(level)
  }
  const handleStatusToggle = (id: number, status: boolean, func: any) => {
    func(
      { id, status },
      {
        onSuccess: (response: ApiResponse<DataObject<{}>>) => {
          toast.success(String(response?.message))
        },
        onError: (error: unknown) => {
          toast.error((error as ApiError)?.message)
        },
      }
    )
  }
  const handleItemAdd = (id: number, level: number) => {
    setCurrentModalLevel(level + 1)
    setSelectedMetaData(null)

    const newArray = [...idArray]
    newArray[level] = id
    setIdArray(newArray)
  }

  return (
    <ConfiguratorContext.Provider
      value={{
        categoryParams,
        setCategoryParams,
        loadingMore,
        setLoadingMore,
        subCategoryParams,
        setSubCategoryParams,
        sectionsParams,
        setSectionsParams,
        sectionFieldsParams,
        setSectionFieldsParams,
        typeParams,
        setTypeParams,
        selectedMetaData,
        setSelectedMetaData,
        selectedTitle,
        setSelectedTitle,
        confirmation,
        setConfirmation,
        currentModalLevel,
        setCurrentModalLevel,
        openModals,
        setOpenModals,
        idArray,
        setIdArray,
        switchToType,
        setSwitchToType,
        copiedItem,
        setCopiedItem,
        handleDelete,
        handleItemDelete,
        handleItemEdit,
        handleStatusToggle,
        handleItemAdd,
        closeAllModals,
        handleOpenModals,
        handleClickEditItem,
        handleModalClose,
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  )
}

export { ConfiguratorProvider, ConfiguratorContext }
